/**
 * VenueMapModal — reusable full-venue map viewer (Phase 4C-2).
 * Canonical asset: /venue-map.png (3000×2382, rendered from Venue_Map.pdf, June 2026).
 * Zoom/pan via react-zoom-pan-pinch. Pins use normalized % coordinates.
 *
 * Props:
 *  onClose()          — required
 *  behavior           — null | {type:"STATION",station} | {type:"PASSED"} | {type:"TABLE_SERVICE",station}
 *  itemName           — optional dish name for context header
 */
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { TransformWrapper, TransformComponent } from "react-zoom-pan-pinch";
import { X, RotateCcw, List, ChevronUp } from "lucide-react";
import {
  MAPS, LEGEND_BY_MAP, getActiveMapId, stationOn, tableOn,
  PASSED_MESSAGE, TABLE_SERVICE_MESSAGE,
} from "../data/venueMapConfig";
import { T } from "../lib/i18n";

// Stable zoom/pan gesture + style configs (avoid new object refs per render)
const TW_DOUBLE_CLICK = { mode: "zoomIn", step: 0.7 };
const TW_WHEEL = { step: 0.15 };
const TW_PINCH = { step: 5 };
const TW_WRAPPER_STYLE = { width: "100%", height: "100%" };
const TW_CONTENT_STYLE = { width: "100%" };

export default function VenueMapModal({ onClose, behavior = null, itemName = "" }) {
  const [legendOpen, setLegendOpen] = useState(false);
  const [pinVisible, setPinVisible] = useState(false);
  const wrapperRef = useRef(null);
  const alreadyLocked = useRef(false);

  const mapId = behavior?.mapId || getActiveMapId();
  const map = MAPS[mapId] || MAPS.A;
  const legend = LEGEND_BY_MAP[mapId] || LEGEND_BY_MAP.A;

  // Scroll lock + Escape (capture phase so an underlying DetailModal doesn't also close)
  useEffect(() => {
    const body = document.body;
    alreadyLocked.current = body.style.position === "fixed";
    const scrollY = window.scrollY;
    let prevOverflow = body.style.overflow;
    if (!alreadyLocked.current) {
      body.style.overflow = "hidden";
    }
    const onKey = (e) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        e.preventDefault();
        onClose();
      }
    };
    window.addEventListener("keydown", onKey, { capture: true });
    return () => {
      window.removeEventListener("keydown", onKey, { capture: true });
      if (!alreadyLocked.current) {
        body.style.overflow = prevOverflow;
        window.scrollTo(0, scrollY);
      }
    };
  }, [onClose]);

  // Resolve pin target from behavior against the central config
  let station = null;
  if (behavior?.type === "MY_TABLE" && behavior.tableNumber != null) {
    const t = tableOn(mapId, behavior.tableNumber);
    station = t ? { ...t, label: behavior.pinLabel || `Table ${behavior.tableNumber}` } : null;
  } else if (behavior?.station) {
    station = behavior.station.station_id
      ? (stationOn(mapId, behavior.station.station_id) || behavior.station)
      : behavior.station;
  }
  const isStation = (behavior?.type === "STATION" || behavior?.type === "MY_TABLE") && station?.x_percent != null;
  const isTable = behavior?.type === "TABLE_SERVICE";
  const isArea = behavior?.type === "AREA";
  const isPassed = behavior?.type === "PASSED";
  const highlightArea = isTable
    ? (station?.area || { x1: 22, y1: 12, x2: 78, y2: 60 })
    : (isArea ? station?.area : null);

  // Zoom toward the pin once map is ready
  useEffect(() => {
    const t = setTimeout(() => {
      setPinVisible(true);
      if ((isStation || isArea) && wrapperRef.current) {
        const el = document.getElementById("venue-map-target-pin") || document.getElementById("venue-map-target-area");
        if (el) wrapperRef.current.zoomToElement(el, isArea ? 1.8 : 2.4, 400);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [isStation, isArea]);

  const resetView = () => {
    wrapperRef.current?.resetTransform(300);
  };

  const message = behavior?.message || (isPassed ? PASSED_MESSAGE : isTable ? TABLE_SERVICE_MESSAGE : null);

  return createPortal(
    <div
      className="fixed inset-0 z-[70] flex flex-col fadein"
      role="dialog"
      aria-modal="true"
      aria-label="Venue map"
      data-testid="venue-map-modal"
      style={{
        background: "rgba(10,10,10,0.96)",
        paddingTop: "env(safe-area-inset-top, 0px)",
        paddingBottom: "env(safe-area-inset-bottom, 0px)",
      }}
    >
      {/* Header */}
      <div className="flex items-center gap-2 px-4 py-3 flex-shrink-0">
        <div className="min-w-0 flex-1">
          <div className="text-[10px] tracking-luxe text-white/60">{T("VENUE MAP")}</div>
          <div className="font-display text-lg text-white leading-tight truncate">
            {behavior?.pinLabel || (isStation ? station.label : itemName || "The Legacy Castle")}
          </div>
          {behavior?.subtitle && (
            <div className="text-xs text-white/70" data-testid="venue-map-subtitle">{behavior.subtitle}</div>
          )}
        </div>
        <button
          onClick={resetView}
          className="flex items-center gap-1.5 px-3 py-2 rounded-full text-xs text-white bg-white/15 hover:bg-white/25 transition flex-shrink-0"
          data-testid="venue-map-reset"
          aria-label="Reset view"
        >
          <RotateCcw className="w-3.5 h-3.5" /> Reset
        </button>
        <button
          onClick={onClose}
          className="w-9 h-9 rounded-full bg-white/90 hover:bg-white flex items-center justify-center shadow-md transition flex-shrink-0"
          aria-label="Close map"
          data-testid="venue-map-close"
        >
          <X className="w-4 h-4 text-black" />
        </button>
      </div>

      {/* Service message banner */}
      {message && (
        <div
          className="mx-4 mb-2 rounded-xl px-4 py-3 text-sm text-white flex-shrink-0"
          style={{ background: "rgba(212,178,128,0.25)", border: "1px solid rgba(212,178,128,0.6)" }}
          data-testid="venue-map-service-message"
        >
          {message}
        </div>
      )}

      {/* Map viewport */}
      <div className="flex-1 min-h-0 mx-3 mb-3 rounded-2xl overflow-hidden bg-white relative" data-testid="venue-map-viewport">
        <TransformWrapper
          ref={wrapperRef}
          minScale={1}
          maxScale={8}
          centerOnInit
          doubleClick={TW_DOUBLE_CLICK}
          wheel={TW_WHEEL}
          pinch={TW_PINCH}
        >
          <TransformComponent
            wrapperStyle={TW_WRAPPER_STYLE}
            contentStyle={TW_CONTENT_STYLE}
          >
            <div className="relative w-full" style={{ aspectRatio: `${map.aspect}` }}>
              <img
                src={map.src}
                alt="The Legacy Castle venue map"
                className="absolute inset-0 w-full h-full select-none"
                draggable={false}
                data-testid="venue-map-image"
                data-map-id={mapId}
              />
              {/* Area highlight (table service / exit / dance floor) */}
              {highlightArea && pinVisible && (
                <div
                  id="venue-map-target-area"
                  className="absolute rounded-xl pointer-events-none fadein"
                  style={{
                    left: `${highlightArea.x1}%`,
                    top: `${highlightArea.y1}%`,
                    width: `${highlightArea.x2 - highlightArea.x1}%`,
                    height: `${highlightArea.y2 - highlightArea.y1}%`,
                    background: "rgba(212,178,128,0.16)",
                    border: "2px solid rgba(180,140,80,0.75)",
                    boxShadow: "0 0 0 4px rgba(212,178,128,0.15)",
                  }}
                  data-testid="venue-map-table-highlight"
                  aria-hidden="true"
                />
              )}
              {/* Area label */}
              {isArea && pinVisible && station && (
                <div
                  className="absolute left-1/2 -translate-x-1/2 whitespace-nowrap text-[9px] font-medium px-1.5 py-0.5 rounded-full bg-black text-white shadow"
                  style={{ left: `${station.x_percent}%`, top: `${(highlightArea?.y1 ?? station.y_percent) - 2}%`, zIndex: 5 }}
                  data-testid="venue-map-area-label"
                >
                  {behavior?.pinLabel || station.label}
                </div>
              )}
              {/* Station pin */}
              {isStation && (
                <div
                  id="venue-map-target-pin"
                  className="absolute"
                  style={{
                    left: `${station.x_percent}%`,
                    top: `${station.y_percent}%`,
                    transform: "translate(-50%, -100%)",
                    opacity: pinVisible ? 1 : 0,
                    transition: "opacity 500ms ease",
                    zIndex: 5,
                  }}
                  data-testid="venue-map-pin"
                  role="img"
                  aria-label={`Map pin: ${behavior?.pinLabel || station.label}`}
                >
                  <MapPinSvg />
                  <div
                    className="absolute left-1/2 -translate-x-1/2 whitespace-nowrap text-[9px] font-medium px-1.5 py-0.5 rounded-full bg-black text-white shadow"
                    style={{ top: "-14px" }}
                    data-testid="venue-map-pin-label"
                  >
                    {behavior?.pinLabel || station.label}
                  </div>
                </div>
              )}
            </div>
          </TransformComponent>
        </TransformWrapper>

        {/* Legend — collapsed by default, never covers the X button */}
        <div className="absolute bottom-2 left-2 z-10 max-w-[75%]">
          {legendOpen ? (
            <div
              className="rounded-xl bg-white/95 shadow-lg p-3 max-h-[45%] overflow-y-auto"
              style={{ maxHeight: "min(260px, 45vh)" }}
              data-testid="venue-map-legend"
            >
              <button
                onClick={() => setLegendOpen(false)}
                className="flex items-center gap-1.5 text-[11px] font-medium text-black mb-2"
                data-testid="venue-map-legend-close"
              >
                <ChevronUp className="w-3.5 h-3.5 rotate-180" /> Legend
              </button>
              <ul className="space-y-1">
                {legend.map((l) => (
                  <li key={l.id} className="flex items-center gap-2 text-[11px] text-black" data-testid={`venue-map-legend-${l.id}`}>
                    <span className="w-6 h-5 rounded bg-black text-white text-[8px] font-bold flex items-center justify-center flex-shrink-0">
                      {l.marker}
                    </span>
                    {l.label}
                  </li>
                ))}
              </ul>
            </div>
          ) : (
            <button
              onClick={() => setLegendOpen(true)}
              className="flex items-center gap-1.5 px-3 py-2 rounded-full text-xs bg-white/95 text-black shadow-lg"
              data-testid="venue-map-legend-toggle"
              aria-label="Open map legend"
            >
              <List className="w-3.5 h-3.5" /> Legend
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}

function MapPinSvg() {
  return (
    <svg width="34" height="44" viewBox="0 0 34 44" aria-hidden="true" style={{ filter: "drop-shadow(0 2px 3px rgba(0,0,0,0.4))" }}>
      <path
        d="M17 1C8.7 1 2 7.7 2 16c0 10.5 13.2 25.4 14 26.2a1.3 1.3 0 0 0 2 0C18.8 41.4 32 26.5 32 16 32 7.7 25.3 1 17 1z"
        fill="#111111"
        stroke="#ffffff"
        strokeWidth="1.5"
      />
      <circle cx="17" cy="16" r="5.5" fill="#ffffff" />
    </svg>
  );
}
